import { Feature, FeatureCollection } from 'geojson';
import { identity } from 'lodash';
import { useMemo } from 'react';
import { featureCollection } from '@turf/turf';
import { useWindowSize } from 'react-use';
import styled from 'styled-components';
import { SoilAttribute, SoilAttributeData } from '../../types/soilAttributes';
import { useIsMobile } from '../../utils/responsive';
import DraggableModal, { Body } from '../basics/DraggableModal';
import ItemsTable from '../tables/ItemsTable';
import { getLayerArea } from '../../utils/layers';

const NoScrollModal = styled(DraggableModal)`
  ${Body} {
    overflow-y: visible;
  }
`;

const ModalTable = styled(ItemsTable)`
  &:first-child {
    width: 100%;
    overflow: visible;
  }
  table {
    overflow-y: auto;
    margin-bottom: 0;
    ${({ $headerAndFooterHeight, $height, $isExpanded, $maxHeight }) =>
      `max-height: ${
        $isExpanded
          ? $maxHeight - $headerAndFooterHeight
          : $height - $headerAndFooterHeight
      }px;`}
  }
  th {
    position: sticky;
    z-index: 1;
  }
`;

interface SoilAttributeTableProps {
  activeAttribute: SoilAttribute;
  activeData?: Nullable<SoilAttributeData>;
  activeLayerFeatureId?: Nullable<string>;
  selectedLayerFeatureIds: string[];
  soilPolygons: FeatureCollection;
  setTableShown: (shown: boolean) => void;
  tableShown: boolean;
}

const SoilAttributeTable = ({
  activeAttribute,
  activeData,
  activeLayerFeatureId,
  selectedLayerFeatureIds,
  soilPolygons,
  setTableShown,
  tableShown,
}: SoilAttributeTableProps) => {
  const { name, cols, getItems = identity } = activeAttribute;
  const tableData = useMemo(() => {
    const featureIds = new Set(selectedLayerFeatureIds);
    if (activeLayerFeatureId) featureIds.add(activeLayerFeatureId);
    const features = featureIds?.size
      ? soilPolygons.features.filter((f: Feature) =>
          featureIds.has(f.properties?.id),
        )
      : soilPolygons.features;
    return featureCollection(features);
  }, [activeLayerFeatureId, selectedLayerFeatureIds, soilPolygons]);
  const totalAcres = useMemo(
    () => getLayerArea({}, soilPolygons || featureCollection([])) || 0,
    [soilPolygons],
  );
  const data = {
    ...(activeData || {}),
    data: tableData,
    totalAcres,
  };

  const isMobile = useIsMobile();
  const windowSize = useWindowSize();
  const startPosition = { top: 60, right: isMobile ? 5 : 266 };
  const width = isMobile ? windowSize.width - 10 : 900;

  return (
    <NoScrollModal
      startPosition={startPosition}
      width={width}
      height={300}
      title={name}
      isOpen={tableShown}
      setIsOpen={setTableShown}
    >
      {({
        headerAndFooterHeight,
        isExpanded,
        maxHeight,
        renderHeader,
        renderBody,
      }: {
        headerAndFooterHeight: number;
        isExpanded: boolean;
        maxHeight: number;
        renderHeader: (headerContent?: JSX.Element) => JSX.Element;
        renderBody: (headerContent?: JSX.Element) => JSX.Element;
      }) => {
        const tableHeader = ({ csvButton }: { csvButton?: JSX.Element } = {}) =>
          renderHeader(csvButton);
        return renderBody(
          <ModalTable
            data={data}
            cols={typeof cols === 'function' ? cols(tableData) : cols}
            msg={activeData?.loading ? 'Loading...' : undefined}
            getItems={getItems}
            tableHeader={tableHeader}
            csvButtonProps={{ className: 'm-0' }}
            csvFileName={name}
            fullWidth
            sortColumn="percentAoi"
            sortDirection="desc"
            $headerAndFooterHeight={headerAndFooterHeight}
            $height={300}
            $isExpanded={isExpanded}
            $maxHeight={maxHeight}
          />,
        );
      }}
    </NoScrollModal>
  );
};

export default SoilAttributeTable;
